import { Box, Grid, Container, Flex } from "@chakra-ui/react";
import { useRouter } from "next/router";
import { useState } from "react";
import { useAPI } from "@/contexts/APIContext";
import { ProfileProvider } from "@/contexts/ProfileContext";
import { COLLEGE_RESULTS_COLORS } from "@/utils/profile";
import Footer from "../../Footer";
import Navbar from "../../Navbar";
import PageInfo from "../../PageInfo";
import { ViewableAONotes } from "../AONotes";
import APScores from "../APScores";
import Awards from "../Awards";
import ECList from "../ECList";
import { ViewableEssayList, ViewableTextDataList } from "../EssayList";
import { ViewableScholarshipList } from "../ScholarshipList";
import {
  ApproachGridArea,
  DecisionsGridArea,
  DecisionType,
  NameGridArea,
  NumericalGridArea,
  PfpGridArea,
} from "./GridAreas";
import { ModalUnlockProfile, ModalUnlockSuccess } from "./ModalUnlockProfile";
import PageProtector from "./PageProtector";
import { Section, SectionColumn, SectionContent, SectionTitle } from "./Section";
import Sidebar from "./Sidebar";
import SimpleTable from "./SimpleTable";
import { logEvent } from "@/utils/analytics";
import { isUnlockAvailable } from "@/utils/unlock-available";
import { useUserData } from "@/contexts/UserDataContext";
import { useClientsideUserData } from "@/contexts/UserDataContext";
import { ModalFreeUnlockIneligible } from "@/components/plans/ModalFreeUnlockEligible";
import { isAdminClientside } from "@/utils/admin/permission-utils";

export const getCurrentPageProfile = (router) => {
  const href = router.asPath;
  // find the regex pattern "profile/____"
  const regex = /profile\/[a-zA-Z0-9]+/;
  const match = href.match(regex);
  if (match) {
    return match[0].split("/")[1];
  } else {
    return null;
  }
};

export default function PageViewProfile({ profileData: profile, ogImageURL, locked }) {
  const router = useRouter();
  const api = useAPI();

  const { currentUser, userData } = useUserData();

  const [unlockHeader, setUnlockHeader] = useState();
  const [showFreeUnlockFailed, setShowFreeUnlockFailed] = useState(false);
  const [unlockSuccess, setUnlockSuccess] = useState(false);
  const expandedDecisionsSection = !profile.approach;

  const headerTemplateAreas = `"nav nav nav" "numbers pfp name" "${
    expandedDecisionsSection ? "decisions decisions" : "approach approach"
  } decisions"`;

  useClientsideUserData();

  const onShowUnlockModal = (header) => {
    if (typeof header === "string") {
      setUnlockHeader(header);
    } else {
      setUnlockHeader("Unlock Profile");
    }
  };

  const onUnlock = async () => {
    setUnlockHeader();

    if (!isUnlockAvailable(userData, profile)) {
      setShowFreeUnlockFailed(true);
      return;
    }

    logEvent(api, "unlockProfile", {
      profileId: getCurrentPageProfile(router),
      profileName: profile.display_name,
      profileNColleges: profile.colleges.length,
    });

    await api.post("users/unlock-profile", true, {
      id: getCurrentPageProfile(router),
    });

    setUnlockSuccess(true);
  };

  profile.id = getCurrentPageProfile(router);

  return (
    <>
      <PageInfo
        title={`${profile.display_name}'s College Applications`}
        overrideOGImage={ogImageURL}
        themeColor="beige"
      />
      <ModalUnlockProfile
        profile={profile}
        header={unlockHeader}
        onClose={() => setUnlockHeader()}
        onUnlock={onUnlock}
      />
      <ModalFreeUnlockIneligible
        open={showFreeUnlockFailed}
        onClose={() => setShowFreeUnlockFailed(false)}
      />
      <ModalUnlockSuccess open={unlockSuccess} />

      <PageProtector isActive={!currentUser || !isAdminClientside(currentUser)}>
        <ProfileProvider profile={profile} isLocked={locked}>
          <Sidebar />

          <Box bg="linear-gradient(to bottom, #fedfef, #e6f6f2)">
            <Box display={{ base: "none", md: "block" }} width="100%" maxW="100%">
              <Box gridArea="nav">
                <Navbar />
              </Box>
              <Container maxW="container.xl">
                <Grid
                  display={{ base: "flex", lg: "grid" }}
                  flexDirection="column"
                  templateAreas={headerTemplateAreas}
                  templateColumns="1fr auto 2fr"
                  rowGap={10}
                  columnGap={10}
                  pb={10}
                  fontFamily="heading"
                  direction="column"
                >
                  <NumericalGridArea />
                  <PfpGridArea />
                  <NameGridArea onClickUnlock={onShowUnlockModal} />
                  <ApproachGridArea />
                  <DecisionsGridArea expanded={expandedDecisionsSection} />
                </Grid>
              </Container>
            </Box>

            <Box display={{ base: "block", md: "none" }}>
              <Navbar />

              <Container display="flex" flexDir="column" gap={8} justifyContent="center" pb={8}>
                <PfpGridArea />
                <NameGridArea onClickUnlock={onShowUnlockModal} />
                <NumericalGridArea />
                <Flex direction="column" align="center" gap={4} mt={4} justify="space-between">
                  <DecisionType
                    result="Accepted"
                    label="Acceptances"
                    color={COLLEGE_RESULTS_COLORS["Accepted"]}
                  />
                  <DecisionType
                    result="Waitlisted"
                    label="Waitlists"
                    color={COLLEGE_RESULTS_COLORS["Waitlisted"]}
                  />
                  <DecisionType
                    result="Rejected"
                    label="Rejections"
                    color={COLLEGE_RESULTS_COLORS["Rejected"]}
                  />
                  <DecisionType
                    result="Withdrawn"
                    label="Withdrawn"
                    color={COLLEGE_RESULTS_COLORS["Withdrawn"]}
                  />
                </Flex>
              </Container>
            </Box>
          </Box>

          <Section bg="red.100" bgSize="100% 100%">
            <SectionColumn id="background">
              <SectionTitle title="Background" />
              <SimpleTable
                data={{
                  Race: profile.ethnicity?.join(", ") ?? "Unknown",
                  Gender: profile.sex,
                  "School Type": profile.highschool_type,
                  Legacy: profile.is_legacy,
                  "Low income": profile.is_low_income,
                  "First generation": profile.is_first_gen,
                  International: profile.is_international,
                  "Recruited Athlete": profile.is_athlete,
                }}
              />
            </SectionColumn>

            <SectionColumn id="academics">
              <SectionTitle title="Academics" />
              <SimpleTable
                data={{
                  "GPA (W)": profile.gpa_w,
                  "GPA (UW)": profile.gpa_uw,
                  "GPA Scale": profile.gpa_scale,
                  SAT: profile.sat_superscore,
                  ACT: profile.act_superscore,
                  "# AP/IB Exams": profile.ap_scores.length,
                }}
              />
            </SectionColumn>
          </Section>

          {profile.essays.length > 0 && (
            <Section bg="pink">
              <SectionColumn spacing={0} w="100%" id="essays">
                <SectionTitle canBeLocked title="Essay(/s)" count={profile.essays.length} />
                <SectionContent whenLocked="essays">
                  <ViewableEssayList onClickLocked={onShowUnlockModal} />
                </SectionContent>
              </SectionColumn>
            </Section>
          )}

          {(profile.ap_scores.length > 0 || profile.awards.length > 0) && (
            <Section bg="purple.100">
              {profile.ap_scores.length > 0 && (
                <SectionColumn id="ap_scores">
                  <SectionTitle
                    canBeLocked
                    title="AP / IB Score(/s)"
                    count={profile.ap_scores.length}
                  />
                  <SectionContent whenLockedImage="ap_scores">
                    <APScores />
                  </SectionContent>
                </SectionColumn>
              )}
              {profile.awards.length > 0 && (
                <SectionColumn id="awards">
                  <SectionTitle canBeLocked title="Award(/s)" count={profile.awards.length} />
                  <SectionContent whenLockedImage="awards">
                    <Awards />
                  </SectionContent>
                </SectionColumn>
              )}
            </Section>
          )}
          {profile.ecs.length > 0 && (
            <Section bg="yellow.50">
              <SectionColumn id="ecs" count={profile.ecs.length}>
                <SectionTitle canBeLocked title="Activit(y/ies)" count={profile.ecs.length} />
                <SectionContent whenLockedImage="ecs">
                  <ECList />
                </SectionContent>
              </SectionColumn>
            </Section>
          )}
          {(profile.additional_info_wc > 0 || profile.advice.length > 0) && (
            <Section bg="green.50" pb={150}>
              {profile.additional_info_wc > 0 && (
                <SectionColumn id="additional_info" flex={1}>
                  <SectionTitle canBeLocked title="Additional Info" />
                  <SectionContent>
                    <ViewableTextDataList
                      sizeLg="3xl"
                      sizeSm="xl"
                      isLocked={locked}
                      data={[
                        {
                          prompt: "Do you have any additional info to share?",
                          content: profile.additional_info,
                          word_count: profile.additional_info_wc,
                        },
                      ]}
                      onClickLocked={onShowUnlockModal}
                    />
                  </SectionContent>
                </SectionColumn>
              )}
              {profile.advice.length > 0 && (
                <SectionColumn id="advice" flex={1}>
                  <SectionTitle canBeLocked count={profile.advice.length} title="Q&A(/s)" />
                  <SectionContent>
                    <ViewableTextDataList
                      isLocked={locked}
                      sizeLg="3xl"
                      sizeSm="xl"
                      data={profile.advice}
                      onClickLocked={onShowUnlockModal}
                    />
                  </SectionContent>
                </SectionColumn>
              )}
            </Section>
          )}
          {(profile.scholarships?.length > 0 || profile.ao_notes?.length > 0) && (
            <Section bg="green.50">
              {profile.scholarships?.length > 0 && (
                <SectionColumn id="scholarships" flex={1}>
                  <SectionTitle
                    canBeLocked
                    count={profile.scholarships.length}
                    title="Scholarship(/s)"
                  />
                  <SectionContent>
                    <ViewableScholarshipList onClickLocked={onShowUnlockModal} />
                  </SectionContent>
                </SectionColumn>
              )}
              {profile.ao_notes?.length > 0 && (
                <SectionColumn id="ao_notes" flex={1}>
                  <SectionTitle
                    canBeLocked
                    count={profile.ao_notes.length}
                    title="Admissions Eval(/s)"
                  />
                  <SectionContent whenLockedImage="ao_notes">
                    <ViewableAONotes />
                  </SectionContent>
                </SectionColumn>
              )}
            </Section>
          )}
        </ProfileProvider>
      </PageProtector>

      <Box marginTop={{ base: -100, md: -150 }}>
        <Footer />
      </Box>
    </>
  );
}
